import React, { useState } from 'react';
import { X, Tag, Plus, Trash2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import type { ToolCategory } from '@shared/types/inventory';

interface CategoryManagerModalProps {
    isOpen: boolean;
    onClose: () => void;
    categories: ToolCategory[];
    onUpdateCategories: (newCategories: ToolCategory[]) => void;
}

export const CategoryManagerModal: React.FC<CategoryManagerModalProps> = ({
    isOpen, onClose, categories, onUpdateCategories
}) => {
    const [newCategory, setNewCategory] = useState('');

    const handleAdd = (e: React.FormEvent) => {
        e.preventDefault();
        const trimmed = newCategory.trim();
        if (trimmed && !categories.some(c => c.toLowerCase() === trimmed.toLowerCase())) {
            const formatted = trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
            onUpdateCategories([...categories, formatted as ToolCategory]);
            setNewCategory('');
        }
    };

    const handleDelete = (catToDelete: ToolCategory) => {
        if (window.confirm(`Supprimer la catégorie "${catToDelete}" ?`)) {
            onUpdateCategories(categories.filter(c => c.toLowerCase() !== catToDelete.toLowerCase()));
        }
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                    onClick={onClose}
                />

                <motion.div
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.95, opacity: 0 }}
                    className="bg-[#1e293b] border border-white/10 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden relative z-10"
                >
                    {/* Header */}
                    <div className="p-4 border-b border-white/10 flex items-center justify-between">
                        <h3 className="font-bold text-white flex items-center gap-2">
                            <Tag size={18} className="text-secondary" />
                            Gérer les Catégories
                        </h3>
                        <button onClick={onClose} className="p-1 hover:bg-white/10 rounded-full transition-colors">
                            <X size={20} className="text-gray-400" />
                        </button>
                    </div>

                    {/* Body */}
                    <div className="p-4 space-y-4">
                        <form onSubmit={handleAdd} className="flex gap-2">
                            <input
                                type="text"
                                value={newCategory}
                                onChange={(e) => setNewCategory(e.target.value)}
                                className="flex-1 bg-black/20 border border-white/10 rounded-lg py-2 px-3 text-sm text-white focus:outline-none focus:border-secondary/50"
                                placeholder="Nouvelle catégorie..."
                            />
                            <button
                                type="submit"
                                disabled={!newCategory.trim()}
                                className="p-2 bg-secondary/20 text-secondary border border-secondary/50 rounded-lg hover:bg-secondary/30 disabled:opacity-50 transition-colors"
                            >
                                <Plus size={20} />
                            </button>
                        </form>

                        <div className="max-h-[300px] overflow-y-auto custom-scrollbar space-y-2">
                            {categories.map((cat) => (
                                <div key={cat} className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/5 hover:border-white/10 group">
                                    <span className="text-sm font-medium text-gray-200">{cat}</span>
                                    <button
                                        onClick={() => handleDelete(cat)}
                                        className="text-gray-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all p-1"
                                        title="Supprimer"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            ))}
                            {categories.length === 0 && (
                                <p className="text-center text-xs text-muted py-4">Aucune catégorie définie.</p>
                            )}
                        </div>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
};
